import { routerMiddleware } from 'react-router-redux';
import { applyMiddleware, createStore, compose } from 'redux';
import { persistStore } from 'redux-persist';
import thunk from 'redux-thunk';
import createSagaMiddleware from 'redux-saga';
import { DEVELOPMENT, SENTRY_IS_ENABLED } from './app/config';
import * as Sentry from '@sentry/react';
import * as LoginActionTypes from 'app/dealer/login/constants';

import { getAppReducer } from './app/reducer';
import { rootSaga } from './app/sagas';

const enhancer = ({ history, sagaMiddleware }) => {
  const routingMiddleware = routerMiddleware(history);
  const functions = [thunk, routingMiddleware, sagaMiddleware];

  const composeEnhancers =
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ && DEVELOPMENT
      ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
          trace: true,
          traceLimit: 25,
        })
      : compose;

  const composeArgs = [applyMiddleware(...functions)];

  if (SENTRY_IS_ENABLED) {
    const loginActionTypes = Object.values(LoginActionTypes);

    const sentryEnhancerArgs = {
      actionTransformer: (action) =>
        loginActionTypes.includes(action.type) ||
        action.type.includes('persist') ||
        action.key === 'dealerLogin'
          ? null
          : action,
      stateTransformer: (state) => ({
        ...state,
        billing: 'SECRET',
        branch: 'SECRET',
        branches: 'SECRET',
        closedLeads: 'SECRET',
        dealersInsights: 'SECRET',
        lead: 'SECRET',
        dealerLogin: 'SECRET',
        paymentMethod: 'SECRET',
      }),
    };

    // https://docs.sentry.io/platforms/javascript/guides/react/integrations/redux/
    composeArgs.push(Sentry.createReduxEnhancer(sentryEnhancerArgs));
  }

  return composeEnhancers(...composeArgs);
};

export function configureStore({
  preloadedState = {},
  history,
  storage,
  callback,
}) {
  const sagaMiddleware = createSagaMiddleware({
    ...(SENTRY_IS_ENABLED && {
      onError: (error, sagaStack) =>
        Sentry.captureException(error, { extras: sagaStack }),
    }),
  });
  const store = createStore(
    getAppReducer(storage),
    preloadedState,
    enhancer({ history, sagaMiddleware })
  );
  const persistor = persistStore(store, {}, callback);

  sagaMiddleware.run(rootSaga);

  return { persistor, store };
}
